/**
 * Shared Presentation — Homeowner view (public, token-scoped).
 *
 * Route: /shared/presentation/:token
 *
 * Milestone 4 · 2026-02-09.
 *
 * Design invariants
 * -----------------
 * • No login. Access is scoped by the URL share token.
 * • Mobile-first. This is the surface a homeowner opens on their phone,
 *   often at the kitchen table with the painter present, so the first
 *   thing they see must feel calm, confident, and painter-branded.
 * • Every homeowner-facing word aligns with:
 *     - PRODUCT_PHILOSOPHY_LOCKED.md ("Designing confidence, not choosing
 *       colours" — reduce uncertainty).
 *     - PLATFORM_POSITIONING_LOCKED.md ("Painters are independent
 *       businesses. ColourMatch is a platform.").
 * • This page is intentionally NOT rendered inside the app chrome
 *   (TopNav / Footer / BottomTabBar) — Layout.jsx already hides them for
 *   any /shared/ path.
 * • Fires POST /shared/presentation/:token/view-ping once on load so the
 *   painter's Studio surfaces "viewed" status + last_viewed_at.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Loader2,
  Palette,
  Download,
  ShieldCheck,
  MapPin,
  Phone,
  Mail,
  Globe,
  ArrowRight,
  Sparkles,
  AlertTriangle,
  ClipboardList,
  Users,
  Quote,
  Shield,
  Wrench,
  Home as HomeIcon,
  Info,
  DollarSign,
  CheckCircle2,
  Pen,
} from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API_BASE, withCredentials: false });

// ---------------------------------------------------------------------------
// Very small, safe markdown → HTML renderer for painter section content.
// We intentionally support only paragraphs, bullet lists, bold and italic —
// anything else is rendered as plain text. This keeps the homeowner surface
// consistent with the painter's Builder preview and blocks XSS surfaces.
// ---------------------------------------------------------------------------
function escape(str) {
  return String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function renderMarkdown(md) {
  if (!md) return null;
  const html = escape(md)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
  const lines = html.split(/\n/);
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        `<ul class="list-disc pl-5 space-y-1.5 my-3">${listBuf
          .map((li) => `<li>${li}</li>`)
          .join("")}</ul>`
      );
      listBuf = [];
    }
  };
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      continue;
    }
    const bullet = trimmed.match(/^[-*]\s+(.+)/);
    if (bullet) {
      listBuf.push(bullet[1]);
      continue;
    }
    flushList();
    out.push(`<p class="my-3 leading-relaxed">${trimmed}</p>`);
  }
  flushList();
  return { __html: out.join("") };
}

function textFromSection(pres, key, fallback = null) {
  const override = pres?.section_overrides?.[key];
  if (override?.markdown) return override.markdown;
  return fallback;
}
function itemsFromSection(pres, key) {
  const override = pres?.section_overrides?.[key];
  return Array.isArray(override?.items) ? override.items : [];
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
export default function SharedPresentation() {
  const { token } = useParams();
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  useEffect(() => {
    document.title = "Your visualisation proposal — ColourMatch";
  }, []);

  useEffect(() => {
    let alive = true;
    async function go() {
      try {
        const { data } = await publicApi.get(`/pro/cms/shared/presentation/${token}`);
        if (!alive) return;
        setBundle(data);
      } catch (e) {
        if (!alive) return;
        setErrorStatus(e?.response?.status || null);
        setError(
          e?.response?.data?.detail ||
            "We couldn't load this presentation. Please check your link and try again."
        );
        return;
      }
      // Fire-and-forget view ping. Never blocks render.
      publicApi.post(`/pro/cms/shared/presentation/${token}/view-ping`).catch(() => {});
    }
    if (token) go();
    return () => {
      alive = false;
    };
  }, [token]);

  const downloadPdf = () => {
    const url = `${API_BASE}/pro/cms/shared/presentation/${token}/pdf`;
    window.open(url, "_blank", "noopener");
  };

  if (error) {
    return <ErrorState status={errorStatus} detail={error} />;
  }
  if (!bundle) {
    return (
      <div
        data-testid="shared-presentation-loading"
        className="min-h-[70vh] flex items-center justify-center bg-brand-linen/40"
      >
        <div className="flex items-center gap-3 text-brand-muted">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading your presentation…</span>
        </div>
      </div>
    );
  }
  return <Presentation bundle={bundle} onDownloadPdf={downloadPdf} />;
}

// ---------------------------------------------------------------------------
// Error / unavailable state — calm and reassuring.
// ---------------------------------------------------------------------------
function ErrorState({ status, detail }) {
  const isGone = status === 404;
  return (
    <div
      className="min-h-[75vh] flex items-center justify-center bg-brand-linen/40 px-6 py-16"
      data-testid="shared-presentation-error"
    >
      <div className="max-w-md w-full bg-white border border-brand-linen rounded-3xl p-8 lg:p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-5">
          {isGone ? <Info className="w-6 h-6" /> : <AlertTriangle className="w-6 h-6" />}
        </div>
        <h1 className="font-serif text-2xl text-brand-ink leading-tight">
          {isGone ? "This presentation is no longer available." : "We couldn't open this presentation."}
        </h1>
        <p className="mt-3 text-sm text-brand-muted leading-relaxed">
          {isGone
            ? "Your painter may have updated it, revoked the link, or replaced it with a newer version. Please contact them directly — they can share the latest link with you."
            : detail || "Please check your link and try again in a moment."}
        </p>
        <div className="mt-6 text-xs text-brand-muted">
          Sent via ColourMatch — a platform that helps homeowners choose colours with confidence
          and connect with independent painters.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Full presentation
// ---------------------------------------------------------------------------
function Presentation({ bundle, onDownloadPdf }) {
  const { presentation: pres, consultation, concepts } = bundle;
  const branding = pres.branding_snapshot || {};
  const included = useMemo(
    () => new Set(pres.included_sections || []),
    [pres.included_sections]
  );
  const painterName = branding.business_name || "Your painter";
  const accent = branding.accent_colour_hex || "#2EB3A0";

  return (
    <div className="bg-brand-linen/30 min-h-screen text-brand-ink" data-testid="shared-presentation">
      <TopBar branding={branding} accent={accent} onDownloadPdf={onDownloadPdf} />

      {included.has("cover") && (
        <CoverSection presentation={pres} consultation={consultation} painterName={painterName} accent={accent} />
      )}

      {included.has("property") && (
        <PropertySection consultation={consultation} />
      )}

      {included.has("company_intro") && (
        <MarkdownSection
          testId="section-company-intro"
          eyebrow="About the painter"
          title={`${painterName} — how they work.`}
          markdown={textFromSection(pres, "company_intro")}
        />
      )}

      {included.has("concepts") && (
        <ConceptsSection concepts={concepts} accent={accent} />
      )}

      {included.has("designer_notes") && (
        <MarkdownSection
          testId="section-designer-notes"
          eyebrow="Designer notes"
          title={`Why these colours work for your home.`}
          icon={Sparkles}
          markdown={
            pres.designer_notes_md ||
            textFromSection(pres, "designer_notes")
          }
        />
      )}

      {included.has("colour_schedule") && (
        <ColourScheduleSection concepts={concepts} />
      )}

      {included.has("recommended_palette") && (
        <RecommendedPaletteSection concepts={concepts} accent={accent} />
      )}

      {included.has("scope_of_works") && (
        <MarkdownSection
          testId="section-scope-of-works"
          eyebrow="Scope of works"
          title="What will be done."
          icon={ClipboardList}
          markdown={textFromSection(pres, "scope_of_works")}
        />
      )}

      {included.has("investment") && (
        <InvestmentSection presentation={pres} accent={accent} />
      )}

      {included.has("terms") && (
        <MarkdownSection
          testId="section-terms"
          eyebrow="Terms & conditions"
          title="Our agreement."
          icon={Shield}
          markdown={textFromSection(pres, "terms")}
          footNote={`Terms are provided by ${painterName} directly. ColourMatch facilitates delivery of this proposal only.`}
        />
      )}

      {included.has("about_us") && (
        <MarkdownSection
          testId="section-about-us"
          eyebrow="About the business"
          title={`Meet ${painterName}.`}
          icon={Users}
          markdown={textFromSection(pres, "about_us")}
        />
      )}

      {included.has("team") && itemsFromSection(pres, "team").length > 0 && (
        <TeamSection items={itemsFromSection(pres, "team")} />
      )}

      {included.has("testimonials") && itemsFromSection(pres, "testimonials").length > 0 && (
        <TestimonialsSection items={itemsFromSection(pres, "testimonials")} />
      )}

      {included.has("warranty") && (
        <MarkdownSection
          testId="section-warranty"
          eyebrow="Warranty"
          title={`Your painter's warranty.`}
          icon={Shield}
          markdown={textFromSection(pres, "warranty")}
          footNote={
            `Warranty is provided directly by ${painterName}, an independent painting business. ColourMatch is not the warrantor.`
          }
        />
      )}

      {included.has("maintenance") && (
        <MarkdownSection
          testId="section-maintenance"
          eyebrow="Care & maintenance"
          title={`Looking after your finish.`}
          icon={Wrench}
          markdown={textFromSection(pres, "maintenance")}
        />
      )}

      {included.has("next_steps") && (
        <MarkdownSection
          testId="section-next-steps"
          eyebrow="Next steps"
          title={`How we move forward.`}
          icon={ArrowRight}
          markdown={
            pres.next_steps_md || textFromSection(pres, "next_steps")
          }
        />
      )}

      {included.has("contact") && (
        <ContactSection branding={branding} painterName={painterName} markdown={textFromSection(pres, "contact")} accent={accent} />
      )}

      {included.has("acceptance") && (
        <AcceptanceSection
          presentation={pres}
          painterName={painterName}
          accent={accent}
          consultation={consultation}
        />
      )}

      <PlatformFooter painterName={painterName} onDownloadPdf={onDownloadPdf} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------
function TopBar({ branding, accent, onDownloadPdf }) {
  return (
    <header
      className="sticky top-0 z-30 backdrop-blur bg-white/85 border-b border-brand-linen"
      data-testid="shared-presentation-topbar"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        {branding.logo_url ? (
          <img
            src={branding.logo_url}
            alt={branding.business_name || "Painter"}
            className="h-8 w-auto object-contain"
          />
        ) : (
          <div
            className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
            style={{ background: accent }}
            aria-hidden="true"
          >
            {(branding.business_name || "P").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div
            className="text-sm sm:text-base font-semibold text-brand-ink truncate"
            data-testid="shared-presentation-brand-name"
          >
            {branding.business_name || "Your painter"}
          </div>
          <div className="text-[11px] uppercase tracking-widest text-brand-muted">
            Via ColourMatch
          </div>
        </div>
        <button
          onClick={onDownloadPdf}
          data-testid="shared-presentation-download-pdf"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold bg-brand-navy text-white hover:bg-brand-teal transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Download PDF</span>
          <span className="sm:hidden">PDF</span>
        </button>
      </div>
    </header>
  );
}

function CoverSection({ presentation, consultation, painterName, accent }) {
  const clientName = (consultation?.client_name || "").split(" ")[0].trim() || "there";
  const cover = presentation.cover_photo_url;
  return (
    <section
      className="relative overflow-hidden border-b border-brand-linen"
      data-testid="shared-presentation-cover"
    >
      {cover ? (
        <div className="relative">
          <img src={cover} alt="Your home" className="w-full h-[52vh] sm:h-[62vh] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/30 to-black/80" />
          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-10 text-white max-w-5xl mx-auto">
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.24em] sm:tracking-[0.3em] mb-2 sm:mb-3" style={{ color: accent }}>
              Visualisation proposal
            </div>
            <h1 className="font-serif text-2xl sm:text-5xl leading-[1.15] sm:leading-tight max-w-2xl">
              Hi {clientName} — here&rsquo;s your coordinated colour plan.
            </h1>
            {presentation.cover_note && (
              <p className="mt-3 sm:mt-4 text-sm sm:text-base text-white/90 max-w-2xl leading-relaxed">
                {presentation.cover_note}
              </p>
            )}
            <p className="mt-4 sm:mt-6 text-xs text-white/70">
              Prepared by <strong className="text-white">{painterName}</strong> · via ColourMatch
            </p>
          </div>
        </div>
      ) : (
        <div
          className="relative px-5 sm:px-10 py-12 sm:py-24 max-w-5xl mx-auto"
          style={{
            background: `linear-gradient(135deg, #0B1E24 0%, #1F3D47 55%, ${accent} 130%)`,
          }}
        >
          <div className="text-white">
            <div className="text-[11px] sm:text-xs uppercase tracking-[0.24em] sm:tracking-[0.3em] mb-2 sm:mb-3" style={{ color: accent }}>
              Visualisation proposal
            </div>
            <h1 className="font-serif text-2xl sm:text-5xl leading-[1.15] sm:leading-tight max-w-2xl text-white">
              Hi {clientName} — here&rsquo;s your coordinated colour plan.
            </h1>
            {presentation.cover_note && (
              <p className="mt-3 sm:mt-4 text-sm sm:text-base text-white/90 max-w-2xl leading-relaxed">
                {presentation.cover_note}
              </p>
            )}
            <p className="mt-4 sm:mt-6 text-xs text-white/70">
              Prepared by <strong className="text-white">{painterName}</strong> · via ColourMatch
            </p>
          </div>
        </div>
      )}
    </section>
  );
}

function PropertySection({ consultation }) {
  if (!consultation) return null;
  const hasAddress = !!consultation.site_address;
  const hasName = !!consultation.name;
  if (!hasAddress && !hasName) return null;
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="section-property">
      <SectionHeader eyebrow="Your project" title={consultation.name || "Your home"} icon={HomeIcon} />
      {hasAddress && (
        <div className="mt-4 inline-flex items-center gap-2 text-sm text-brand-muted bg-white border border-brand-linen rounded-full px-4 py-2">
          <MapPin className="w-3.5 h-3.5" />
          <span>{consultation.site_address}</span>
        </div>
      )}
    </section>
  );
}

function ConceptsSection({ concepts, accent }) {
  if (!concepts?.length) return null;
  return (
    <section
      className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
      data-testid="section-concepts"
    >
      <SectionHeader
        eyebrow="Colour concepts"
        title={concepts.length > 1 ? `${concepts.length} coordinated concepts to consider.` : "A coordinated concept for your home."}
        icon={Palette}
      />
      <div className="mt-8 space-y-8">
        {concepts.map((c) => (
          <ConceptCard key={c.id} concept={c} accent={accent} />
        ))}
      </div>
    </section>
  );
}

function ConceptCard({ concept, accent }) {
  const surfaces = concept.surfaces || [];
  const swatches = surfaces.filter((s) => s.colour_hex);
  const hero = concept.hero_render_url || null;
  return (
    <article
      className="bg-white border border-brand-linen rounded-3xl overflow-hidden shadow-soft"
      data-testid={`concept-card-${concept.id}`}
    >
      {/* Hero band — either a real per-concept render (if the painter has
          uploaded one) or a bold multi-colour band that stands in as the
          visual signature for this concept. Concept name overlays the band
          so the homeowner immediately knows what they're looking at. */}
      {hero ? (
        <div className="relative h-52 sm:h-72 bg-brand-linen">
          <img src={hero} alt={concept.name || "Concept"} className="w-full h-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-5 sm:p-6">
            <div className="text-[10px] uppercase tracking-widest font-bold text-white/80 mb-1">
              Colour concept
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight">
              {concept.name || "Concept"}
            </h3>
          </div>
        </div>
      ) : swatches.length > 0 ? (
        <div className="relative h-40 sm:h-52">
          <div className="absolute inset-0 flex">
            {swatches.slice(0, 6).map((s, i) => (
              <div
                key={i}
                className="flex-1"
                style={{ background: s.colour_hex }}
                title={s.colour_name || s.colour_code || ""}
                aria-label={`${s.surface_display_name || "Surface"} — ${s.colour_name || ""}`}
              />
            ))}
          </div>
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent p-5 sm:p-6">
            <div className="text-[10px] uppercase tracking-widest font-bold text-white/85 mb-1">
              Colour concept
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight drop-shadow-sm">
              {concept.name || "Concept"}
            </h3>
          </div>
        </div>
      ) : null}
      <div className="p-5 sm:p-7">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            {/* Only render title here if the hero band wasn't already showing it */}
            {!hero && swatches.length === 0 && (
              <h3 className="font-serif text-2xl text-brand-ink leading-tight">
                {concept.name || "Concept"}
              </h3>
            )}
            {concept.description && (
              <p className={`${!hero && swatches.length === 0 ? "mt-2 " : ""}text-sm text-brand-muted leading-relaxed max-w-xl`}>
                {concept.description}
              </p>
            )}
          </div>
          <span
            className="text-[11px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full"
            style={{ background: `${accent}15`, color: accent }}
          >
            {surfaces.length} surface{surfaces.length === 1 ? "" : "s"}
          </span>
        </div>
        {surfaces.length > 0 && (
          <ul className="mt-5 divide-y divide-brand-linen">
            {surfaces.map((s, i) => (
              <li
                key={`${concept.id}-${s.surface_slug}-${i}`}
                className="py-2.5 flex items-center gap-3"
                data-testid={`surface-row-${s.surface_slug}`}
              >
                <span
                  className="w-8 h-8 rounded-lg border border-brand-linen flex-shrink-0"
                  style={{
                    background: s.colour_hex || "#F5F1E6",
                  }}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-brand-ink capitalize truncate">
                    {s.surface_display_name || s.surface_slug}
                  </div>
                  <div className="text-xs text-brand-muted truncate">
                    {s.colour_name ? s.colour_name : "Not yet assigned"}
                    {s.colour_code ? ` · ${s.colour_code}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

function ColourScheduleSection({ concepts }) {
  const rows = [];
  for (const c of concepts || []) {
    for (const s of c.surfaces || []) {
      if (!s.colour_code) continue;
      rows.push({
        concept: c.name || "Concept",
        surface: s.surface_display_name || s.surface_slug,
        hex: s.colour_hex,
        name: s.colour_name,
        code: s.colour_code,
      });
    }
  }
  if (!rows.length) return null;
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="section-colour-schedule">
      <SectionHeader eyebrow="Colour schedule" title="Every surface, every colour, in one place." icon={ClipboardList} />
      <div className="mt-6 bg-white border border-brand-linen rounded-3xl overflow-hidden">
        <div className="hidden sm:grid grid-cols-12 text-[11px] uppercase tracking-widest text-brand-muted px-5 py-3 border-b border-brand-linen">
          <div className="col-span-3">Concept</div>
          <div className="col-span-4">Surface</div>
          <div className="col-span-5">Colour</div>
        </div>
        <ul className="divide-y divide-brand-linen">
          {rows.map((r, i) => (
            <li key={i} className="px-5 py-3 sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center">
              <div className="text-xs text-brand-muted sm:col-span-3">{r.concept}</div>
              <div className="text-sm text-brand-ink font-medium capitalize sm:col-span-4">{r.surface}</div>
              <div className="mt-1 sm:mt-0 sm:col-span-5 flex items-center gap-2.5">
                <span className="w-6 h-6 rounded border border-brand-linen flex-shrink-0" style={{ background: r.hex || "#F5F1E6" }} />
                <span className="text-sm truncate">
                  {r.name || "—"}
                  {r.code ? <span className="text-brand-muted"> · {r.code}</span> : null}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function TeamSection({ items }) {
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="section-team">
      <SectionHeader eyebrow="The team" title="Who's on the tools." icon={Users} />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((m, i) => (
          <div key={i} className="bg-white border border-brand-linen rounded-3xl p-5 flex items-start gap-4">
            {m.photo_url ? (
              <img src={m.photo_url} alt={m.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-brand-linen text-brand-ink flex items-center justify-center font-semibold flex-shrink-0">
                {(m.name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-semibold text-brand-ink truncate">{m.name}</div>
              {m.role && <div className="text-xs text-brand-muted mt-0.5">{m.role}</div>}
              {m.bio && <p className="text-sm text-brand-muted mt-2 leading-relaxed">{m.bio}</p>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TestimonialsSection({ items }) {
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="section-testimonials">
      <SectionHeader eyebrow="From past clients" title="What homeowners say." icon={Quote} />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {items.map((t, i) => (
          <blockquote key={i} className="bg-white border border-brand-linen rounded-3xl p-6">
            <Quote className="w-4 h-4 text-brand-teal mb-3" />
            <p className="text-sm text-brand-ink leading-relaxed">{t.quote}</p>
            <footer className="mt-4 text-xs text-brand-muted">
              — <span className="font-semibold text-brand-ink">{t.author}</span>
              {t.source ? ` · ${t.source}` : ""}
            </footer>
          </blockquote>
        ))}
      </div>
    </section>
  );
}

function ContactSection({ branding, painterName, markdown, accent }) {
  const rows = [
    branding?.phone && { icon: Phone, label: branding.phone, href: `tel:${branding.phone}` },
    branding?.email && { icon: Mail, label: branding.email, href: `mailto:${branding.email}` },
    branding?.website && { icon: Globe, label: branding.website, href: branding.website },
  ].filter(Boolean);
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="section-contact">
      <SectionHeader eyebrow="Get in touch" title={`Ready to talk with ${painterName}?`} icon={Phone} />
      {markdown && (
        <div
          className="mt-5 text-brand-ink prose prose-sm max-w-3xl"
          dangerouslySetInnerHTML={renderMarkdown(markdown)}
        />
      )}
      {rows.length > 0 && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {rows.map((r, i) => (
            <a
              key={i}
              href={r.href}
              className="bg-white border border-brand-linen rounded-2xl px-4 py-4 flex items-center gap-3 hover:border-brand-teal transition-colors"
              data-testid={`contact-row-${i}`}
            >
              <span
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: `${accent}15`, color: accent }}
              >
                <r.icon className="w-4 h-4" />
              </span>
              <span className="text-sm text-brand-ink truncate">{r.label}</span>
            </a>
          ))}
        </div>
      )}
      <p className="mt-6 text-xs text-brand-muted leading-relaxed max-w-2xl">
        Any quotes, warranties, or work agreements come directly from {painterName}, an independent
        painting business. ColourMatch is the platform that helps you find and coordinate colours with them.
      </p>
    </section>
  );
}

function MarkdownSection({ testId, eyebrow, title, icon, markdown, footNote }) {
  if (!markdown) return null;
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid={testId}>
      <SectionHeader eyebrow={eyebrow} title={title} icon={icon} />
      <div
        className="mt-6 text-brand-ink prose prose-sm max-w-3xl leading-relaxed"
        dangerouslySetInnerHTML={renderMarkdown(markdown)}
      />
      {footNote && (
        <p className="mt-4 text-[11px] text-brand-muted italic max-w-2xl leading-relaxed">
          {footNote}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Recommended Colour Palette — large swatches across concepts.
// Aggregates every unique surface colour from every concept into one
// "recommended palette" strip so the homeowner can see the full colour
// story at a glance. Never inferred from anything else — pure aggregation.
// ---------------------------------------------------------------------------
function RecommendedPaletteSection({ concepts = [], accent }) {
  const swatches = useMemo(() => {
    const map = new Map();
    (concepts || []).forEach((c) => {
      (c.surfaces || []).forEach((s) => {
        const hex = (s.hex || s.colour_hex || "").toUpperCase();
        if (!hex) return;
        const existing = map.get(hex);
        if (existing) {
          existing.surfaces.add(s.surface_label || s.surface_slug || "");
          return;
        }
        map.set(hex, {
          hex,
          name: s.colour_name || s.name || "Untitled",
          code: s.colour_code || s.library_code || "",
          finish: s.finish || "",
          surfaces: new Set([s.surface_label || s.surface_slug || ""]),
        });
      });
    });
    return Array.from(map.values());
  }, [concepts]);

  if (swatches.length === 0) return null;

  return (
    <section
      className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
      data-testid="section-recommended-palette"
    >
      <SectionHeader
        eyebrow="Recommended palette"
        title="Every colour in one place."
        icon={Palette}
      />
      <p className="mt-3 text-sm text-brand-muted max-w-2xl leading-relaxed">
        These are the exact ColourMatch colours proposed for your project. Each swatch
        shows the finish and where it will be used.
      </p>
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {swatches.map((s) => (
          <div
            key={s.hex}
            className="bg-white rounded-2xl border border-brand-linen overflow-hidden shadow-soft"
            data-testid={`recommended-swatch-${s.hex.replace("#", "")}`}
          >
            <div
              className="aspect-square w-full"
              style={{ background: s.hex }}
              aria-label={`${s.name} — ${s.hex}`}
            />
            <div className="p-4">
              <div className="font-serif text-lg text-brand-ink leading-tight">
                {s.name}
              </div>
              <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-brand-muted">
                {s.hex} {s.code ? `· ${s.code}` : ""}
              </div>
              {s.finish && (
                <div className="mt-2 text-xs text-brand-muted">Finish: {s.finish}</div>
              )}
              {s.surfaces.size > 0 && (
                <div className="mt-2 text-[11px] text-brand-ink/70 leading-snug">
                  {Array.from(s.surfaces).filter(Boolean).slice(0, 3).join(" · ")}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Investment — structured pricing block.
// Reads the painter-supplied numbers from `section_overrides.investment.attributes.investment`
// (InvestmentDetails shape). Falls back to a soft "to be confirmed" state so
// a painter can publish before pricing is finalised without ever showing an
// empty $0 to the homeowner.
// ---------------------------------------------------------------------------
function InvestmentSection({ presentation: pres, accent }) {
  const override = pres?.section_overrides?.investment || {};
  const inv = override?.attributes?.investment || {};
  const total = typeof inv.total_aud === "number" ? inv.total_aud : null;
  const items = Array.isArray(inv.line_items) ? inv.line_items : [];
  const gstInclusive = inv.gst_inclusive !== false;
  const deposit = typeof inv.deposit_pct === "number" ? inv.deposit_pct : null;
  const validUntil = inv.valid_until || null;
  const notes = override?.markdown || null;

  const depositAmount = total != null && deposit != null ? Math.round(total * (deposit / 100)) : null;
  const balanceAmount = total != null && depositAmount != null ? Math.round(total - depositAmount) : null;

  const fmt = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 })
      : "—";

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="section-investment">
      <SectionHeader eyebrow="Investment" title="Your project total." icon={DollarSign} />
      <div className="mt-6 bg-brand-navy text-white rounded-3xl overflow-hidden shadow-soft">
        {/* Premium summary card — mobile: Total is hero full-width, then 2x2 grid.
            Desktop (lg): all 5 KPIs in one row for the wide-screen "spec sheet" feel. */}
        <div className="lg:hidden">
          {/* Mobile hero — Total Investment gets its own row */}
          <div className="px-6 pt-8 pb-6 border-b border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-brand-teal mb-2 font-bold">
              Total investment
            </div>
            <div className="font-serif text-4xl leading-none" data-testid="investment-total-mobile">
              {total != null ? fmt(total) : "TBC"}
            </div>
            {total != null && (
              <div className="mt-2 text-xs text-white/70">
                {gstInclusive ? "GST inclusive" : "Plus GST"}
              </div>
            )}
          </div>
          {/* Mobile 2x2 grid — remaining KPIs */}
          <div className="grid grid-cols-2 gap-y-6 px-6 py-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-teal mb-1.5 font-bold">Deposit</div>
              <div className="text-sm">{depositAmount != null ? `${fmt(depositAmount)} (${deposit}%)` : (deposit != null ? `${deposit}%` : "—")}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-teal mb-1.5 font-bold">Balance</div>
              <div className="text-sm">{balanceAmount != null ? fmt(balanceAmount) : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-teal mb-1.5 font-bold">GST</div>
              <div className="text-sm">{total != null ? (gstInclusive ? "Inclusive" : "Plus GST") : "—"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-teal mb-1.5 font-bold">Valid until</div>
              <div className="text-sm">{validUntil || "—"}</div>
            </div>
          </div>
        </div>
        {/* Desktop — all 5 KPIs in one row */}
        <div className="hidden lg:grid grid-cols-5 gap-y-6 px-10 py-8">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-teal mb-2 font-bold">
              Total investment
            </div>
            <div className="font-serif text-4xl leading-none" data-testid="investment-total">
              {total != null ? fmt(total) : "TBC"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-teal mb-2 font-bold">GST</div>
            <div className="text-base" data-testid="investment-gst">{total != null ? (gstInclusive ? "Inclusive" : "Plus GST") : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-teal mb-2 font-bold">Deposit</div>
            <div className="text-base" data-testid="investment-deposit">
              {depositAmount != null ? `${fmt(depositAmount)} (${deposit}%)` : (deposit != null ? `${deposit}%` : "—")}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-teal mb-2 font-bold">Balance</div>
            <div className="text-base" data-testid="investment-balance">{balanceAmount != null ? fmt(balanceAmount) : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand-teal mb-2 font-bold">Valid until</div>
            <div className="text-base">{validUntil || "—"}</div>
          </div>
        </div>
      </div>
      {items.length > 0 && (
        <div className="mt-6 sm:mt-8 bg-white border border-brand-linen rounded-2xl overflow-hidden shadow-soft">
          <div className="px-5 sm:px-10 py-5 sm:py-6">
            <div className="overline text-brand-teal mb-3">Breakdown</div>
            <ul className="divide-y divide-brand-linen" data-testid="investment-line-items">
              {items.map((li, idx) => (
                <li key={idx} className="py-3 flex items-center justify-between text-sm gap-3">
                  <span className="text-brand-ink truncate">{li.label}</span>
                  <span className="font-mono text-brand-ink flex-shrink-0">{fmt(li.amount_aud)}</span>
                </li>
              ))}
              {total != null && (
                <li className="py-3 flex items-center justify-between text-brand-ink font-semibold gap-3 border-t-2 border-brand-navy">
                  <span>Total</span>
                  <span className="font-mono">{fmt(total)}</span>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
      {notes && (
        <div
          className="mt-5 sm:mt-6 text-sm text-brand-ink prose prose-sm max-w-3xl leading-relaxed"
          dangerouslySetInnerHTML={renderMarkdown(notes)}
        />
      )}
      <p className="mt-5 sm:mt-6 text-[11px] text-brand-muted italic max-w-2xl leading-relaxed">
        Investment is set by your painter directly. ColourMatch does not process this
        payment or set the price — it simply presents the numbers here so you can
        review them alongside the colour plan.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Acceptance — electronic signature. Locked flow (2026-02 platform direction):
// name + email + date + timestamp. Once signed the section flips to the
// signed-state confirmation card (immutable).
// ---------------------------------------------------------------------------
function AcceptanceSection({ presentation: pres, painterName, accent, consultation }) {
  const { token } = useParams();
  const initial = pres.acceptance || null;
  const [state, setState] = useState({
    signed: !!initial,
    submitting: false,
    error: null,
    record: initial,
    name: (consultation?.client_name || "").trim(),
    email: "",
    agreed: false,
  });

  const override = pres?.section_overrides?.acceptance || {};
  const bodyMarkdown = override?.markdown || null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (state.submitting) return;
    if (!state.name.trim() || !state.email.trim() || !state.agreed) {
      setState((s) => ({ ...s, error: "Please add your name, email and tick the confirmation box." }));
      return;
    }
    setState((s) => ({ ...s, submitting: true, error: null }));
    try {
      const { data } = await publicApi.post(
        `/pro/cms/shared/presentation/${token}/accept`,
        {
          client_name: state.name.trim(),
          client_email: state.email.trim().toLowerCase(),
          concept_id: pres.selected_concept_id || null,
        }
      );
      setState((s) => ({
        ...s,
        submitting: false,
        signed: true,
        record: data.acceptance,
      }));
    } catch (err) {
      setState((s) => ({
        ...s,
        submitting: false,
        error:
          err?.response?.data?.detail ||
          "We couldn't record your acceptance. Please try again in a moment.",
      }));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <section className="bg-white border-t border-brand-linen" data-testid="section-acceptance">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <SectionHeader
          eyebrow="Acceptance"
          title={state.signed ? "Proposal accepted — thank you." : "Ready to move forward?"}
          icon={state.signed ? CheckCircle2 : Pen}
        />
        {bodyMarkdown && (
          <div
            className="mt-4 text-sm text-brand-ink prose prose-sm max-w-2xl leading-relaxed"
            dangerouslySetInnerHTML={renderMarkdown(bodyMarkdown)}
          />
        )}

        {state.signed ? (
          <div
            className="mt-8 max-w-2xl bg-brand-teal/5 border border-brand-teal/30 rounded-2xl p-6 sm:p-8"
            data-testid="acceptance-signed-card"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-brand-teal flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-serif text-xl text-brand-ink">
                  Accepted by {state.record?.client_name}
                </div>
                <div className="mt-1 text-xs text-brand-muted">
                  {state.record?.client_email}
                </div>
                <div className="mt-3 text-sm text-brand-ink">
                  Signed on <strong>{fmtDate(state.record?.signed_at)}</strong>
                </div>
                <p className="mt-4 text-xs text-brand-muted leading-relaxed">
                  {painterName} has been notified. They&rsquo;ll be in touch to schedule the next step.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-8 max-w-2xl bg-white border border-brand-linen rounded-2xl p-6 sm:p-8 shadow-soft"
            data-testid="acceptance-form"
          >
            <p className="text-sm text-brand-muted leading-relaxed">
              By signing below, you confirm you&rsquo;d like to proceed with the colour plan and
              scope of works as outlined in this proposal. This is not a binding contract —
              your painter will follow up to finalise dates, deposit, and any remaining
              details.
            </p>

            <label className="block mt-6">
              <span className="text-xs uppercase tracking-widest text-brand-muted font-semibold">
                Full name
              </span>
              <input
                type="text"
                value={state.name}
                onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
                data-testid="acceptance-input-name"
                className="mt-2 block w-full h-12 px-4 rounded-xl border border-brand-linen focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 outline-none text-brand-ink text-base"
                placeholder="Jane Homeowner"
                autoComplete="name"
                required
              />
            </label>

            <label className="block mt-4">
              <span className="text-xs uppercase tracking-widest text-brand-muted font-semibold">
                Email
              </span>
              <input
                type="email"
                value={state.email}
                onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
                data-testid="acceptance-input-email"
                className="mt-2 block w-full h-12 px-4 rounded-xl border border-brand-linen focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/20 outline-none text-brand-ink text-base"
                placeholder="jane@example.com"
                autoComplete="email"
                required
              />
            </label>

            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={state.agreed}
                onChange={(e) => setState((s) => ({ ...s, agreed: e.target.checked }))}
                data-testid="acceptance-input-agree"
                className="mt-1 w-4 h-4 accent-brand-teal cursor-pointer"
              />
              <span className="text-sm text-brand-ink leading-relaxed">
                I accept the colour plan, scope of works and investment outlined in this
                proposal from {painterName}, and agree to be contacted to schedule the work.
              </span>
            </label>

            {state.error && (
              <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {state.error}
              </div>
            )}

            <button
              type="submit"
              disabled={state.submitting || !state.name.trim() || !state.email.trim() || !state.agreed}
              data-testid="acceptance-submit"
              className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-brand-navy text-white font-semibold text-sm hover:bg-brand-teal transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {state.submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Signing…</>
              ) : (
                <><Pen className="w-4 h-4" /> Sign & accept proposal</>
              )}
            </button>

            <p className="mt-4 text-[11px] text-brand-muted italic leading-relaxed">
              Your signature is timestamped and shared with {painterName}. ColourMatch stores
              only your name, email, and the acceptance timestamp — never payment details.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, icon: Icon }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && (
        <div className="w-9 h-9 rounded-xl bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0 mt-0.5">
          <Icon className="w-4 h-4" />
        </div>
      )}
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">
          {eyebrow}
        </div>
        <h2 className="mt-1 font-serif text-2xl sm:text-3xl text-brand-ink leading-tight">
          {title}
        </h2>
      </div>
    </div>
  );
}

function PlatformFooter({ painterName, onDownloadPdf }) {
  return (
    <footer
      className="mt-12 border-t border-brand-linen bg-white"
      data-testid="shared-presentation-footer"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
        <div className="text-xs text-brand-muted max-w-lg leading-relaxed">
          {painterName} is an independent painting business using the ColourMatch platform.
          ColourMatch helps homeowners choose colours with confidence and connect with independent
          painters. It does not employ, franchise, manage, or represent painters.
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onDownloadPdf}
            data-testid="shared-presentation-footer-download"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-xs font-semibold border border-brand-linen hover:border-brand-teal text-brand-ink transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Download PDF
          </button>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-brand-muted">
            <ShieldCheck className="w-3 h-3 text-brand-teal" />
            ColourMatch
          </div>
        </div>
      </div>
    </footer>
  );
}
